import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FaPrint, FaFileAlt, FaImage, 
  FaListUl, FaCheckCircle, FaChevronRight,
  FaTimes, FaFileInvoice, FaMoneyBillWave
} from 'react-icons/fa';

export default function ProformaInvoiceReview() {
  const { id } = useParams();
  const [showFormatSelection, setShowFormatSelection] = useState(true);
  const [printType, setPrintType] = useState('full');
  const [loading, setLoading] = useState(true);
  const [proformaInvoice, setProformaInvoice] = useState(null);

  const printTypes = [
    {
      id: 'basic',
      title: 'Basic Print',
      description: 'Simple proforma invoice without brand names, MRP, and product images',
      icon: <FaListUl size={24} />,
      color: 'green'
    },
    {
      id: 'medium',
      title: 'Standard Print',
      description: 'Includes product images but excludes brand names and MRP',
      icon: <FaImage size={24} />,
      color: 'blue'
    },
    {
      id: 'full',
      title: 'Detailed Print',
      description: 'Complete proforma invoice with all details including brands, MRP, and images',
      icon: <FaFileAlt size={24} />,
      color: 'indigo'
    },
    {
      id: 'discount',
      title: 'Discount Print',
      description: 'Detailed proforma invoice with discount column showing difference between listed and offer prices',
      icon: <FaFileInvoice size={24} />,
      color: 'orange'
    },
    {
      id: 'project',
      title: 'Project Print',
      description: 'Detailed proforma invoice with GST inclusive prices shown in brackets',
      icon: <FaCheckCircle size={24} />,
      color: 'purple'
    },
    {
      id: 'payment',
      title: 'Payment Details Print',
      description: 'Proforma invoice with payment status, advance amount, and balance details highlighted',
      icon: <FaMoneyBillWave size={24} />,
      color: 'red'
    }
  ];

  useEffect(() => {
    const fetchProformaInvoiceData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/proforma-invoice/data/${id}`);
        if (response.data?.data) {
          setProformaInvoice(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching proforma invoice:', error);
        toast.error('Failed to fetch proforma invoice data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProformaInvoiceData();
  }, [id]);

  const generatePdfUrl = (type) => {
    // Use different template based on quotation type
    let templateName = 'rlugroup-proforma-invoice.hbs';
    
    if (proformaInvoice?.quotationType === 'tally-quotation') {
      templateName = 'rlugroup-tally-proforma-invoice.hbs';
    } else if (proformaInvoice?.quotationType === 'quotation-new') {
      templateName = 'rlugroup-new-proforma-invoice.hbs';
    }
    
    return `https://rlugroup.in/pdf/convert?fileName=${templateName}&url=${process.env.REACT_APP_API_BASE_URL}/proforma-invoices/print/${id}?printType=${type}`;
  };

  const handleTypeSelect = (type) => {
    setPrintType(type);
    setShowFormatSelection(false);
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'TOTAL_CREDIT':
        return 'text-red-600';
      case 'ADVANCE_PARTIAL':
        return 'text-yellow-600';
      case 'FULL_PAID':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'text-yellow-600';
      case 'PARTIAL':
        return 'text-blue-600';
      case 'COMPLETED':
        return 'text-green-600';
      case 'CANCELLED':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const FormatSelectionModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-11/12 md:w-3/4 lg:w-2/3 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold flex items-center">
            <FaPrint className="mr-2 text-gray-600" />
            Select Print Format
          </h3>
          <button 
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={() => setShowFormatSelection(false)}
          >
            <FaTimes size={24} />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {printTypes.map((type) => (
            <div 
              key={type.id}
              className={`border-2 border-${type.color}-200 rounded-lg p-4 cursor-pointer hover:border-${type.color}-400 hover:shadow-lg transition-all duration-200 bg-gradient-to-br from-${type.color}-50 to-white`}
              onClick={() => handleTypeSelect(type.id)}
            >
              <div className={`text-${type.color}-600 mb-3`}>
                {type.icon}
              </div>
              <h4 className={`font-semibold text-${type.color}-800 mb-2`}>
                {type.title}
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                {type.description}
              </p>
              <div className={`flex items-center justify-center text-${type.color}-600 font-medium hover:text-${type.color}-800`}>
                Select Format
                <FaChevronRight className="ml-2" size={12} />
              </div>
            </div>
          ))}
        </div>

        {/* Proforma Invoice Preview */}
        {loading ? (
          <div className="mt-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading proforma invoice details...</p>
          </div>
        ) : proformaInvoice && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-3">Proforma Invoice Preview</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Invoice Number</div>
                <div className="font-medium">{proformaInvoice.invoiceNumber}</div>
              </div>
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Recipient</div>
                <div className="font-medium text-sm">
                  {proformaInvoice.quotation?.recipient?.name || proformaInvoice.quotation?.recipient?.businessName || 'N/A'}
                </div>
              </div>
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Items</div>
                <div className="font-medium">{proformaInvoice.items?.length || 0}</div>
              </div>
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Total Amount</div>
                <div className="font-medium text-green-600">
                  ₹{proformaInvoice.totalAmount?.toLocaleString('en-IN') || '0'}
                </div>
              </div>
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Payment Status</div>
                <div className={`font-medium ${getPaymentStatusColor(proformaInvoice.paymentStatus)}`}>
                  {proformaInvoice.paymentStatus?.replace('_', ' ') || 'N/A'}
                </div>
              </div>
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Status</div>
                <div className={`font-medium ${getStatusColor(proformaInvoice.status)}`}>
                  {proformaInvoice.status || 'N/A'}
                </div>
              </div>
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Quotation Type</div>
                <div className="font-medium text-blue-600">
                  {proformaInvoice.quotationType === 'quotation-new' ? 'New Quotation' : 'Tally Quotation'}
                </div>
              </div>
              <div className="p-3 bg-white rounded shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Expected Delivery</div>
                <div className="font-medium text-sm">
                  {proformaInvoice.expectedDeliveryDate 
                    ? new Date(proformaInvoice.expectedDeliveryDate).toLocaleDateString('en-IN')
                    : 'N/A'
                  }
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Format Selection Modal - Shows by default */}
      {showFormatSelection && <FormatSelectionModal />}

      {/* PDF Viewer */}
      <div className={`h-screen w-full ${showFormatSelection ? 'hidden' : 'block'}`}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <object
            data={generatePdfUrl(printType)}
            type="application/pdf"
            className="w-full h-full"
            aria-label="Proforma Invoice PDF"
          >
            <div className="text-center p-8">
              <p className="mb-4">Your browser doesn't support embedded PDFs.</p>
              <a 
                href={generatePdfUrl(printType)} 
                target="_blank" 
                rel="noreferrer"
              >
                Download the PDF
              </a>
            </div>
          </object>
        )}
      </div>
    </>
  );
}
